import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { byValue, occurrence } from '@Containers/ResultsFilters/ResultsFiltersConstants';

import { Flyout } from '@els/els-react--flyout';
import { ButtonWithIcon } from '@els/els-react--button';
import { RadioButtonGroup } from '@els/els-react--radio-button';

import ClearSelectedButton from './ClearSelectedButton';
import SortingButtons from './SortingButtons';
import { sortingOptionsTypeMap } from '../BarChartCheckboxesDialogConstants';
import SelectedItemsCount from '../../SelectedItemsCount';

import './BarChartCheckboxesDialogHeader.scss';

function BarChartCheckboxesDialogHeader(props) {
  const {
    filterTitle,
    selectedCount,
    customGroups,
    customGroupsRadioButtonsFeatureEnabled,
    sortingType,
    onClickClearSelected,
    onOccurrence,
    onByValue,
  } = props;
  const showCustomGroupsRadioButtons = !!customGroups && customGroupsRadioButtonsFeatureEnabled;
  const [isOpenSortingTypeDropdown, handleSortingTypeDropdown] = useState(false);
  const flyoutRef = useRef(null);
  const sortTypeMap = {
    occurrence: 'Occurrence',
    byValue: 'Alphanumerical',
  };
  useEffect(() => {
    function handleClickOutside(event) {
      if (flyoutRef.current && !flyoutRef.current.contains(event.target)) {
        handleSortingTypeDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  return (
    <>
      <h2 className="bar-chart-checkboxes-dialog-header-title" id="dialog-title">
        {filterTitle}
      </h2>
      <hr className="u-els-margin-top-1o2 bar-chart-checkboxes-header-divider" />
      {!showCustomGroupsRadioButtons && (
        <div className="bar-chart-checkboxes-dialog-header-controls-wrapper u-els-margin-top u-els-margin-bottom">
          <section className="section-left">
            <section>
              <SelectedItemsCount count={selectedCount} showZero />
              <span className={classnames('selected-items-count-label', 'u-els-margin-left-1o4')}>selected</span>
            </section>
            <ClearSelectedButton {...props} />
          </section>
          <section className="section-right">
            <Flyout
              flyout={
                <div ref={flyoutRef} className="flyout-container">
                  <section>
                    <RadioButtonGroup
                      options={[sortingOptionsTypeMap[occurrence], sortingOptionsTypeMap[byValue]]}
                      onChange={(t) => {
                        if (t.target.value === sortingOptionsTypeMap[occurrence]) {
                          onOccurrence();
                        } else if (t.target.value === sortingOptionsTypeMap[byValue]) {
                          onByValue();
                        }
                        onClickClearSelected();
                        handleSortingTypeDropdown(false);
                      }}
                      value={sortingType && sortTypeMap[sortingType]}
                      a11y={{ name: 'Sort filters' }}
                    />
                  </section>
                  <section>
                    <ButtonWithIcon
                      type="link"
                      iconOnly
                      sprite="Close"
                      data-automationId="bar-chart-dialog-header-dropdown-close"
                      id="bar-chart-dialog-header-sorting-dropdown-close"
                      onClick={() => handleSortingTypeDropdown(false)}>
                      close
                    </ButtonWithIcon>
                  </section>
                </div>
              }
              pointerType="primary"
              shouldCloseOnESC
              width="250px"
              placement="bottom-end"
              isControlled
              isOpen={isOpenSortingTypeDropdown}>
              <ButtonWithIcon
                type="link"
                sprite={isOpenSortingTypeDropdown ? 'ChevronUp' : 'ChevronDown'}
                data-automationId="bar-chart-dialog-header-sorting-dropdown"
                id="bar-chart-dialog-header-sorting-dropdown"
                iconRight
                onClick={() => handleSortingTypeDropdown(!isOpenSortingTypeDropdown)}>
                {`Sort by ${sortingOptionsTypeMap[sortingType]}`}
              </ButtonWithIcon>
            </Flyout>
            <SortingButtons {...props} />
          </section>
        </div>
      )}
      {showCustomGroupsRadioButtons && (
        <div className="bar-chart-checkboxes-dialog-header-controls-wrapper u-els-margin-top u-els-margin-bottom">
          <section className="section-left">
            <SelectedItemsCount count={selectedCount} showZero />
            <span className="bar-chart-checkboxes-dialog-header-selected">selected</span>
            <ClearSelectedButton {...props} />
            <SortingButtons {...props} />
          </section>
          <section className="section-right">
            <RadioButtonGroup
              isInline
              options={[sortingOptionsTypeMap[occurrence], sortingOptionsTypeMap[byValue]]}
              onChange={(t) => {
                if (t.target.value === sortingOptionsTypeMap[occurrence]) {
                  onOccurrence();
                } else if (t.target.value === sortingOptionsTypeMap[byValue]) {
                  onByValue();
                }
                onClickClearSelected();
                handleSortingTypeDropdown(false);
              }}
              value={sortingType && sortTypeMap[sortingType]}
              a11y={{ name: 'Sort by:' }}
            />
          </section>
        </div>
      )}
    </>
  );
}

BarChartCheckboxesDialogHeader.propTypes = {
  filterTitle: PropTypes.string.isRequired,
  onClickClearSelected: PropTypes.func.isRequired,
  onAscending: PropTypes.func.isRequired,
  onDescending: PropTypes.func.isRequired,
  disableAscending: PropTypes.bool.isRequired,
  disableDescending: PropTypes.bool.isRequired,
  onByValue: PropTypes.func.isRequired,
  onOccurrence: PropTypes.func.isRequired,
  sortingType: PropTypes.oneOf([occurrence, byValue]).isRequired,
  customGroups: PropTypes.bool,
  selectedCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  customGroupsRadioButtonsFeatureEnabled: PropTypes.bool,
  activeChunk: PropTypes.array,
};

export default BarChartCheckboxesDialogHeader;
